import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { gql, useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAltOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import MusicNoteOutlinedIcon from '@mui/icons-material/MusicNoteOutlined';
import ContentCutIcon from '@mui/icons-material/ContentCutOutlined';
import KeyboardIcon from '@mui/icons-material/KeyboardOutlined';
import RefreshIcon from '@mui/icons-material/RefreshOutlined';
import { useAuth } from '../context/AuthContext';
import { useProjectAuth } from '../utils/authUtils';
import { safeArray } from '../utils/arrayUtils';
import {
  downloadAsset,
  exportAnnotationsJson,
  generateProjectTasks,
  generateProjectZip,
  respondToProjectAssignment,
  updateRevisionStatus,
} from '../graphqlService';
import ProjectNotes from './ProjectNotes';
import UploadRevisionModal from './UploadRevisionModal';
import MulticamReviewPlayer from './Multicam/MulticamReviewPlayer';
import { recordingAngle, transcriptSourceOf } from './Multicam/separateAudio';
import AssetPreviewModal from './AssetPreviewModal';
import TaskManagementPanel from './TaskManagementPanel/TaskManagementPanel';
import InteractiveTimeline from './InteractiveTimeline/InteractiveTimeline';
import ProTimeline from './Timeline/ProTimeline';
import type { TimelineLane } from './Timeline/timelineTypes';
import { useMediaPlayer } from './Timeline/useMediaPlayer';
import TransportBar from './AnnotationStudio/TransportBar';
import VideoStage, { useFullscreen } from './AnnotationStudio/VideoStage';
import AnnotationPanel from './AnnotationStudio/AnnotationPanel';
import MarkerExportMenu from './AnnotationStudio/MarkerExportMenu';
import FrameRateChip from './AnnotationStudio/FrameRateChip';
import ShortcutHelpDialog, { ShortcutGroup } from './AnnotationStudio/ShortcutHelpDialog';
import { StudioAnnotation, adjacentAnnotationTime, annotationsActiveAt, isRangeAnnotation } from './AnnotationStudio/annotationModel';
import WorkspaceHeader from './EditorWorkspace/WorkspaceHeader';
import TaskBoard, { TaskCard } from './EditorWorkspace/TaskBoard';
import TaskFormDialog, { TaskFormValues } from './EditorWorkspace/TaskFormDialog';
import AssetsTab, { WorkspaceAsset } from './EditorWorkspace/AssetsTab';
import DeliverTab from './EditorWorkspace/DeliverTab';
import BriefTab from './EditorWorkspace/BriefTab';
import { TaskStatusChoice, WorkspaceTask, summarizeTasks, taskEnd, taskStart, taskStatusMeta } from './EditorWorkspace/taskMeta';
import { awaitingEditorResponse, buildRevisionVersions, nextStepGuidance } from './EditorWorkspace/workflow';
import { brand, neutral } from '../theme';
import Section from './ui/Section';
import LoadingState from './ui/LoadingState';
import { friendlyError } from './Projects/projectListModel';
import { debugLog } from '../utils/debugLog';
import { getAnnotationCategory } from '../utils/annotationCategories';
import { getApolloErrorMessage } from '../utils/apolloError';
import { downloadTextFile } from '../utils/download';
import { DEFAULT_FPS, formatTimecode } from '../utils/timecode';
import { formatDate } from '../utils/format';
import { isFinishedStatus, resolveRevisionLimit } from '../utils/projectStatus';
import type { ReviewAnnotation } from './Multicam/multicamReview';
import TranscriptPanel from './Transcript/TranscriptPanel';
import { createSpeakerLookup } from '../utils/speakerActivity';
import NotesTranscriptSwitch from './Transcript/NotesTranscriptSwitch';
import type { TranscriptMarkingProps } from './Transcript/transcriptMarking';
import { transcriptMarksFrom } from '../utils/transcriptMarks';

// GraphQL Queries and Mutations
export const GET_PROJECT_DETAILS = gql`
  query GetProjectDetails($id: ID!) {
    editingRequest(id: $id) {
      videoId
      id
      projectName
      videoName
      videoUrl
      videoDuration
      description
      comments
      brandGuidelines
      musicPreference
      colorPreference
      targetAudience
      desiredStyle
      deliverables
      referenceLinks
      referenceFiles
      status
      createdAt
      updatedAt
      userId
      editorId
      deadline
      priority
      estimatedComplexity
      editor {
        id
        name
        email
        status
      }
      tasks {
        id
        title
        description
        type
        status
        priority
        timeline {
          start
          end
          frameStart
          frameEnd
        }
        assetRefs
        assetInstructions
        aiInstructions
        estimatedMinutes
        actualMinutes
        dependencies
        tags
        editorNotes
        reviewComments
        generatedFromAnnotations
        createdAt
        updatedAt
        completedAt
      }
      taskProgress {
        completionPercentage
        completedTasks
        totalTasks
        inProgressTasks
        estimatedTotalMinutes
        actualTotalMinutes
        lastUpdated
      }
      images {
        id
        position
        startTime
        endTime
        url
        notes
      }
      sounds {
        id
        startTime
        endTime
        url
        notes
      }
      annotations {
        id
        text
        time
        endTime
        category
        color
      }
      notes {
        id
        message
        authorId
        authorName
        authorRole
        timestamp
        type
      }
      editedVideoPath
      revisionCount
      revisionLimit
      creatorName
      revisions {
        id
        filename
        createdBy
        createdByName
        createdAt
        status
        previewUrl
        notes
        sizeBytes
      }
      sourceType
      multicamSetId
      multicamSnapshot {
        snapshotId
        name
        audioAngleKey
        timelineDurationMs
        angles {
          angleKey
          videoId
          label
          videoName
          videoUrl
          sizeBytes
          durationMs
          timelineStartMs
          sourceInMs
          sourceOutMs
          speakerName
        }
        speakingActivity { version frameMs durationMs angleKeys confident reason runs basis }
        audioSource {
          videoId
          label
          videoName
          videoUrl
          durationMs
          timelineStartMs
          sourceInMs
          sourceOutMs
          channels
          sampleRate
        }
      }
      multicamAnnotationsSnapshotId
      sourceFrameRate
      canEdit
      canEditTasks
    }
  }
`;

export const UPDATE_TASK_STATUS = gql`
  mutation UpdateTaskStatus($projectId: String!, $taskId: String!, $status: TaskStatus!, $notes: String) {
    updateTaskStatus(projectId: $projectId, taskId: $taskId, status: $status, notes: $notes) {
      id
      status
      completedAt
      editorNotes
      updatedAt
    }
  }
`;

const CREATE_TASK = gql`
  mutation AddCustomTask($projectId: String!, $task: TaskInput!) {
    addCustomTask(projectId: $projectId, task: $task) {
      id
      title
      description
      type
      status
      priority
      timeline {
        start
        end
        frameStart
        frameEnd
      }
      assetRefs
      assetInstructions
      aiInstructions
      estimatedMinutes
      actualMinutes
      dependencies
      tags
      editorNotes
      createdAt
      updatedAt
      completedAt
    }
  }
`;

const UPDATE_TASK = gql`
  mutation UpdateTask($projectId: String!, $taskId: String!, $updates: TaskUpdateInput!) {
    updateTask(projectId: $projectId, taskId: $taskId, updates: $updates) {
      id
      title
      description
      type
      status
      priority
      timeline {
        start
        end
        frameStart
        frameEnd
      }
      assetRefs
      assetInstructions
      aiInstructions
      estimatedMinutes
      actualMinutes
      dependencies
      tags
      editorNotes
      createdAt
      updatedAt
      completedAt
    }
  }
`;

const DELETE_TASK = gql`
  mutation DeleteTask($taskId: String!) {
    deleteTask(taskId: $taskId) {
      success
      message
    }
  }
`;

const TAB_KEYS = ['feedback', 'tasks', 'timeline', 'assets', 'deliver', 'brief', 'messages'] as const;

/** Browser storage key for the editor's multicam pointer (transcript and rough cut). */
const MULTICAM_HINT_KEY = 'pithplay.hint.multicam-editor';
type TabKey = typeof TAB_KEYS[number];
const isTabKey = (value: unknown): value is TabKey => (TAB_KEYS as ReadonlyArray<unknown>).includes(value);
/** The tab named in the URL; "notes" is the Messages tab's old name. */
const tabFromUrl = (value: string | null): TabKey | null =>
  value === 'notes' ? 'messages' : isTabKey(value) ? value : null;

const FPS_STORAGE_PREFIX = 'pithplay.workspace.fps.';

// Build-time flag, so it lives outside the component (and outside hook deps).
const debugAutoGen =
  process.env.NODE_ENV !== 'production' &&
  process.env.REACT_APP_DEBUG_AUTOGEN === '1';

const WORKSPACE_SHORTCUTS: ShortcutGroup[] = [
  {
    title: 'Playback',
    items: [
      { keys: ['Space'], label: 'Play / pause' },
      { keys: ['J', 'K', 'L'], label: 'Shuttle back / stop / forward' },
      { keys: ['←', '→'], label: 'Back / forward 5 seconds (Shift: 10)' },
      { keys: [',', '.'], label: 'Previous / next frame' },
      { keys: ['M'], label: 'Mute' },
      { keys: ['F'], label: 'Full screen' },
    ],
  },
  {
    title: 'Feedback',
    items: [
      { keys: ['[', ']'], label: 'Previous / next note' },
      { keys: ['Enter'], label: 'Jump to the focused note or timeline item' },
    ],
  },
  {
    title: 'Timeline',
    items: [
      { keys: ['+', '−'], label: 'Zoom in / out (Ctrl / ⌘ + scroll)' },
      { keys: ['Alt', '← →'], label: 'Nudge the focused task one frame' },
      { keys: ['?'], label: 'Show these shortcuts' },
    ],
  },
];

const isTypingTarget = (target: EventTarget | null) => {
  const el = target as HTMLElement | null;
  if (!el || typeof el.closest !== 'function') return false;
  if (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)) return true;
  return Boolean(el.closest('[role="combobox"], [role="listbox"], [role="menu"], [role="dialog"]'));
};

const readStoredFps = (projectId?: string): number | null => {
  if (!projectId) return null;
  try {
    const value = Number(window.localStorage.getItem(`${FPS_STORAGE_PREFIX}${projectId}`));
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
};

const tabLabel = (label: string, count?: React.ReactNode) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
    {label}
    {count !== undefined && count !== null && (
      <Box
        component="span"
        sx={{ px: 0.75, minWidth: 20, height: 20, borderRadius: '10px', bgcolor: 'grey.200', color: 'text.secondary', fontSize: 11.5, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontVariantNumeric: 'tabular-nums' }}
      >
        {count}
      </Box>
    )}
  </Box>
);

/**
 * The editor's project workspace: feedback with a frame-accurate player and
 * timeline, tasks generated from the creator's notes, an editable task
 * timeline, assets and marker exports, delivery of revisions, the brief, and
 * messages. Creators can open it too (read-mostly: they review revisions here).
 */
const EditProjects: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  // The project list's "Suggest task guidance" lands here and opens the preview.
  const openAIPreviewRequested = Boolean(
    (location.state as { openAIPreview?: boolean } | null)?.openAIPreview
  );

  const initialTab = (): TabKey => {
    const fromUrl = tabFromUrl(new URLSearchParams(location.search).get('tab'));
    if (fromUrl) return fromUrl;
    return openAIPreviewRequested ? 'tasks' : 'feedback';
  };
  const [tab, setTabState] = useState<TabKey>(initialTab);
  const [visitedTabs, setVisitedTabs] = useState<Set<TabKey>>(() => new Set([initialTab()]));

  const [taskUpdating, setTaskUpdating] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [uploadRevisionModalOpen, setUploadRevisionModalOpen] = useState(false);
  const [assetPreview, setAssetPreview] = useState<any>(null);
  const [assetBusy, setAssetBusy] = useState<Record<string, boolean>>({});
  const [taskDialog, setTaskDialog] = useState<{ open: boolean; task: WorkspaceTask | null }>({ open: false, task: null });
  const [taskToDelete, setTaskToDelete] = useState<string | null>(null);
  const [savingTask, setSavingTask] = useState(false);
  const [packageBusy, setPackageBusy] = useState(false);
  const [packageInfo, setPackageInfo] = useState<{ sizeBytes?: number; expiresAt?: string } | null>(null);
  const [responding, setResponding] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [declineError, setDeclineError] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);
  const [inspectedTaskId, setInspectedTaskId] = useState<string | null>(null);
  // A frame rate the user picked for this project. Used only while the
  // source rate is unknown: the rate read from the file wins (see `fps`).
  const [fpsOverride, setFpsOverride] = useState<number | null>(() => readStoredFps(id));

  // Auto task generation state
  const [autoGenerating, setAutoGenerating] = useState(false);
  // Project id we have already attempted auto task generation for.
  const autoGenAttemptedRef = useRef<string | null>(null);
  // The same, as state: read during render, it always updates in the same
  // batch as `autoGenerating` (the ref can be set a render earlier).
  const [autoGenAttemptedFor, setAutoGenAttemptedFor] = useState<string | null>(null);
  // Generation reported new tasks but the refetched list has not rendered yet.
  const [awaitingGeneratedTasks, setAwaitingGeneratedTasks] = useState(false);

  // Multicam annotations (editor-side viewer)
  const [multicamAnnotationsExport, setMulticamAnnotationsExport] = useState<null | {
    version: string;
    exportedAt: string;
    multicamSetName: string;
    annotationCount: number;
    annotations: Array<{
      timelineTimeMs: number;
      endTimeMs: number | null;
      type: string;
      targetAngleKey: string | null;
      text: string;
      category: string | null;
      color: string | null;
    }>;
  }>(null);
  const [multicamAnnotationsLoading, setMulticamAnnotationsLoading] = useState(false);
  const [multicamAnnotationsError, setMulticamAnnotationsError] = useState<string | null>(null);
  // Clicking a multicam note seeks the review player to it.
  const [multicamSeek, setMulticamSeek] = useState<{ timeMs: number; nonce: number } | null>(null);
  const [multicamClock, setMulticamClock] = useState<{ ms: number; playing: boolean }>({ ms: 0, playing: false });
  // The editor's pointer to the transcript and the rough cut; closing it hides it in this browser.
  const [multicamHintDismissed, setMulticamHintDismissed] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(MULTICAM_HINT_KEY) === 'dismissed';
    } catch {
      return false;
    }
  });
  const dismissMulticamHint = () => {
    setMulticamHintDismissed(true);
    try {
      window.localStorage.setItem(MULTICAM_HINT_KEY, 'dismissed');
    } catch {
      // Not remembered; it shows again next visit.
    }
  };

  const fullscreen = useFullscreen<HTMLDivElement>();
  const timelineZoomRef = useRef<{ zoomIn: () => void; zoomOut: () => void; fit: () => void } | null>(null);

  const setTab = useCallback((next: TabKey) => {
    setTabState(next);
    setVisitedTabs((previous) => (previous.has(next) ? previous : new Set(previous).add(next)));
    const params = new URLSearchParams(location.search);
    params.set('tab', next);
    navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true, state: location.state });
  }, [location.pathname, location.search, location.state, navigate]);

  // GraphQL Hooks
  const { data, loading, error: queryError, refetch } = useQuery(GET_PROJECT_DETAILS, {
    variables: { id },
    skip: !id,
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true
  });

  // The source's frame rate as recorded at upload (from the project or its
  // video). When known it is a fact about the footage; otherwise the user's
  // choice, or the default, labelled as assumed.
  const sourceFps = (() => {
    const rate = Number(data?.editingRequest?.sourceFrameRate);
    return Number.isFinite(rate) && rate > 0 ? rate : null;
  })();
  const fpsKnown = sourceFps !== null;
  const fps = sourceFps ?? fpsOverride ?? DEFAULT_FPS;
  const player = useMediaPlayer({ fps });

  const writeTaskIntoCache = (cache: any, patch: any) => {
    if (!patch || !id) return;
    const existingData = cache.readQuery({ query: GET_PROJECT_DETAILS, variables: { id } }) as any;
    if (!existingData?.editingRequest?.tasks) return;
    const updatedTasks = existingData.editingRequest.tasks.map((task: any) =>
      task.id === patch.id ? { ...task, ...patch } : task
    );
    const completedCount = updatedTasks.filter((t: any) => t.status === 'COMPLETED').length;
    const inProgressCount = updatedTasks.filter((t: any) => t.status === 'IN_PROGRESS').length;
    const totalTasks = updatedTasks.length;
    cache.writeQuery({
      query: GET_PROJECT_DETAILS,
      variables: { id },
      data: {
        ...existingData,
        editingRequest: {
          ...existingData.editingRequest,
          tasks: updatedTasks,
          taskProgress: existingData.editingRequest.taskProgress
            ? {
                ...existingData.editingRequest.taskProgress,
                completedTasks: completedCount,
                inProgressTasks: inProgressCount,
                totalTasks,
                completionPercentage: totalTasks > 0 ? Math.round((completedCount / totalTasks) * 100) : 0,
                lastUpdated: new Date().toISOString()
              }
            : existingData.editingRequest.taskProgress
        }
      }
    });
  };

  const [updateTaskMutation] = useMutation(UPDATE_TASK, {
    update: (cache, { data: result }) => writeTaskIntoCache(cache, result?.updateTask),
    onError: (mutationError) => {
      console.error('Task update failed:', mutationError);
      setError(friendlyError(mutationError, 'The task wasn’t saved. Please try again.'));
    }
  });

  const [updateTaskStatusMutation] = useMutation(UPDATE_TASK_STATUS, {
    optimisticResponse: (variables) => ({
      updateTaskStatus: {
        id: variables.taskId,
        status: variables.status,
        completedAt: variables.status === 'COMPLETED' ? new Date().toISOString() : null,
        editorNotes: variables.notes || '',
        updatedAt: new Date().toISOString(),
        __typename: 'EditorTask'
      }
    }),
    update: (cache, { data: result }) => writeTaskIntoCache(cache, result?.updateTaskStatus),
    onError: (mutationError) => {
      console.error('Task status update failed:', mutationError);
      setError(friendlyError(mutationError, 'The task’s status wasn’t changed. Please try again.'));
    }
  });
  const [createTaskMutation] = useMutation(CREATE_TASK);
  const [deleteTaskMutation] = useMutation(DELETE_TASK);

  // Extract project from query data
  const project = data?.editingRequest || null;

  // Safely extract arrays (handle null values from backend)
  const tasks: WorkspaceTask[] = safeArray(project?.tasks);
  const images: WorkspaceAsset[] = safeArray(project?.images);
  const sounds: WorkspaceAsset[] = safeArray(project?.sounds);
  const notes: any[] = safeArray(project?.notes);
  const revisions: any[] = safeArray(project?.revisions);
  const projectAnnotations = project?.annotations;
  const annotations: StudioAnnotation[] = useMemo(
    () => safeArray<any>(projectAnnotations)
      .filter((annotation) => annotation && Number.isFinite(Number(annotation.time)))
      .map((annotation, index) => ({
        id: String(annotation.id || `note-${index}`),
        time: Number(annotation.time),
        text: String(annotation.text ?? ''),
        category: annotation.category || 'general',
        color: annotation.color || undefined,
        ...(typeof annotation.endTime === 'number' && annotation.endTime > annotation.time ? { endTime: annotation.endTime } : {}),
      }))
      .sort((a, b) => a.time - b.time),
    [projectAnnotations]
  );
  const annotationsById = useMemo(() => new Map(annotations.map((a) => [a.id, a])), [annotations]);

  // Use secure authorization utilities
  const { canEdit, userRole } = useProjectAuth(user, project);

  // Delivered and cancelled projects are finished: reading and downloads stay
  // available, but nothing on this page may change the project any more. A
  // project whose status has not loaded yet is not treated as finished.
  const isFinished = isFinishedStatus(project?.status);
  const readOnlyNotice = isFinished
    ? `This project is ${String(project?.status).toUpperCase() === 'CANCELLED' ? 'cancelled' : 'delivered'} — it's read-only now.`
    : null;
  const canEditTaskList = project?.canEditTasks === true || canEdit;
  const isMulticam = project?.sourceType === 'MULTICAM';

  const loadMulticamAnnotationsExport = async () => {
    if (!project || project.sourceType !== 'MULTICAM') return;
    if (!project.multicamAnnotationsSnapshotId) return;

    setMulticamAnnotationsLoading(true);
    setMulticamAnnotationsError(null);
    try {
      const result = await exportAnnotationsJson(project.id);
      setMulticamAnnotationsExport(result);
    } catch (err: any) {
      console.error('Failed to load multicam annotations export:', err);
      setMulticamAnnotationsError('The creator’s multicam notes didn’t load. Use the refresh button by the notes to try again.');
      setMulticamAnnotationsExport(null);
    } finally {
      setMulticamAnnotationsLoading(false);
    }
  };

  useEffect(() => {
    // Only load the multicam annotations snapshot for multicam projects.
    if (!project || project.sourceType !== 'MULTICAM' || !project.multicamAnnotationsSnapshotId) {
      setMulticamAnnotationsExport(null);
      setMulticamAnnotationsError(null);
      return;
    }
    loadMulticamAnnotationsExport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id, project?.sourceType, project?.multicamAnnotationsSnapshotId]);

  // Handle query errors
  const projectLoaded = Boolean(data?.editingRequest);
  useEffect(() => {
    if (queryError) {
      console.error('Project query error:', queryError);
      setError(
        projectLoaded
          ? 'Some details of this project didn’t load. Refresh the page to try again.'
          : 'We couldn’t load this project. Check your connection and try again.'
      );
    }
  }, [queryError, projectLoaded]);

  // Auto-generate tasks when annotations exist but no tasks
  const hasSingleAnnotations = safeArray(project?.annotations).length > 0;
  const hasMulticamAnnotations = isMulticam && !!project?.multicamAnnotationsSnapshotId;
  const hasAnyAnnotations = hasSingleAnnotations || hasMulticamAnnotations;
  const hasTasks = tasks.length > 0;
  const autoGenWillRun = Boolean(
    project &&
    project.canEditTasks &&
    user &&
    hasAnyAnnotations &&
    !hasTasks &&
    !isFinished &&
    autoGenAttemptedFor !== project.id
  );
  // An "Enhance with AI" request waits for these tasks instead of failing (ED-18).
  const tasksPending = autoGenerating || autoGenWillRun || (awaitingGeneratedTasks && !hasTasks);

  useEffect(() => {
    if (hasTasks && awaitingGeneratedTasks) setAwaitingGeneratedTasks(false);
  }, [hasTasks, awaitingGeneratedTasks]);

  useEffect(() => {
    const shouldAutoGenerate = !!(
      project &&
      project.canEditTasks &&
      !autoGenerating &&
      user &&
      hasAnyAnnotations &&
      !hasTasks
    );

    if (debugAutoGen) {
      debugLog('AUTO-GEN DEBUG:', {
        projectId: project?.id,
        canEdit,
        projectCanEditTasks: project?.canEditTasks,
        autoGenerating,
        sourceType: project?.sourceType,
        tasksCount: tasks.length,
        shouldAutoGenerate
      });
    }

    // One attempt per project, ever. The backend reports success even when it
    // generated zero tasks (it still bumps updatedAt), so the refetch produced
    // a new project object with annotations and still no tasks — re-running
    // this effect forever. A ref is used rather than state so the guard is set
    // before any re-render can schedule a second attempt.
    if (project?.id && autoGenAttemptedRef.current === project.id) {
      return;
    }

    // A finished project must not get a fresh task list just for being viewed.
    if (!project || !project.canEditTasks || autoGenerating || !user || isFinishedStatus(project.status)) {
      return;
    }

    if (!hasAnyAnnotations || hasTasks) {
      return;
    }

    (async () => {
      autoGenAttemptedRef.current = project.id;
      setAutoGenAttemptedFor(project.id);
      setAutoGenerating(true);
      try {
        const token = await user.getIdToken(true);
        const result = await generateProjectTasks(project.id, token);
        if (debugAutoGen) debugLog('GENERATION RESULT:', result);

        if (result.success) {
          // Nothing to generate from these annotations: don't refetch, or the
          // new project object would just re-trigger this effect.
          if (result.tasksGenerated !== 0) {
            // The refetched list can render after autoGenerating clears.
            setAwaitingGeneratedTasks(true);
            await refetch();
          }
        } else {
          console.error('Task generation failed:', result.message);
          setError(result.message || 'Tasks couldn’t be generated from the feedback. Use “Generate tasks from feedback” to try again.');
        }
      } catch (generationError: any) {
        setAwaitingGeneratedTasks(false);
        console.error('Auto task generation error:', generationError);
        setError(friendlyError(generationError, 'Tasks couldn’t be generated from the feedback. Use “Generate tasks from feedback” to try again.'));
      } finally {
        setAutoGenerating(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, canEdit, autoGenerating, user, refetch]);

  const openCreateTask = () => {
    if (isFinished) return;
    setTaskDialog({ open: true, task: null });
  };

  const openEditTask = useCallback((task: WorkspaceTask) => {
    if (isFinished) return;
    setTaskDialog({ open: true, task });
  }, [isFinished]);

  const handleSaveTask = async (values: TaskFormValues) => {
    if (!project || !id || isFinished || savingTask) return;
    setSavingTask(true);
    const editing = taskDialog.task;
    try {
      if (!editing) {
        const result = await createTaskMutation({
          variables: {
            projectId: id,
            task: {
              title: values.title,
              description: values.description,
              type: values.type,
              priority: values.priority,
              estimatedMinutes: values.estimatedMinutes,
              editorNotes: values.editorNotes,
              timeline: { start: values.start, end: values.end },
              assetRefs: [],
              assetInstructions: [],
              dependencies: [],
              tags: []
            }
          }
        });
        if (result.data?.addCustomTask) {
          setSuccessMessage('Task created');
          await refetch();
          setTaskDialog({ open: false, task: null });
        }
      } else {
        const result = await updateTaskMutation({
          variables: {
            projectId: id,
            taskId: editing.id,
            updates: {
              title: values.title,
              description: values.description,
              type: values.type,
              priority: values.priority,
              estimatedMinutes: values.estimatedMinutes,
              editorNotes: values.editorNotes,
              timeline: { start: values.start, end: values.end },
              ...(values.actualMinutes !== null ? { actualMinutes: values.actualMinutes } : {})
            }
          }
        });
        if (result.data?.updateTask) {
          setSuccessMessage('Task saved');
          setTaskDialog({ open: false, task: null });
        }
      }
    } catch (saveError: any) {
      console.error('Failed to save task:', saveError);
      setError(friendlyError(saveError, editing ? 'The task wasn’t saved. Please try again.' : 'The task wasn’t created. Please try again.'));
    } finally {
      setSavingTask(false);
    }
  };

  const confirmDeleteTask = async () => {
    if (!taskToDelete || !project || !id || isFinished) return;
    const targetId = taskToDelete;
    setTaskUpdating((previous) => ({ ...previous, [targetId]: true }));
    setError(null);
    try {
      const result = await deleteTaskMutation({ variables: { taskId: targetId } });
      if (result.data?.deleteTask?.success) {
        await refetch();
        setSuccessMessage('Task deleted');
      } else {
        setError(result.data?.deleteTask?.message || 'The task wasn’t deleted. Please try again.');
      }
    } catch (deleteError: any) {
      console.error('Failed to delete task:', deleteError);
      setError(friendlyError(deleteError, 'The task wasn’t deleted. Please try again.'));
    } finally {
      setTaskUpdating((previous) => ({ ...previous, [targetId]: false }));
      setTaskToDelete(null);
    }
  };

  const handleTaskStatusChange = useCallback(async (taskId: string, newStatus: TaskStatusChoice) => {
    if (!project || !id || isFinished) return;
    setTaskUpdating((previous) => ({ ...previous, [taskId]: true }));
    setError(null);
    try {
      const result = await updateTaskStatusMutation({
        variables: { projectId: id, taskId, status: newStatus, notes: '' }
      });
      if (result.data?.updateTaskStatus) {
        setSuccessMessage(newStatus === 'COMPLETED' ? 'Task marked done' : `Task moved to ${taskStatusMeta(newStatus).label}`);
      }
    } catch (statusError: any) {
      console.error('Failed to update task status:', statusError);
      setError(friendlyError(statusError, 'The task’s status wasn’t changed. Please try again.'));
    } finally {
      setTaskUpdating((previous) => ({ ...previous, [taskId]: false }));
    }
  }, [project, id, isFinished, updateTaskStatusMutation]);

  const handleDownloadPackage = async () => {
    if (!project || !id || packageBusy) return;
    setPackageBusy(true);
    setError(null);
    try {
      const token = await user?.getIdToken(true);
      if (!token) return;
      // generateProjectZip builds the archive synchronously and resolves
      // with a ready download URL - there is no async job to track.
      const zipResult = await generateProjectZip(id, token);
      if (zipResult.success && zipResult.downloadUrl) {
        setPackageInfo({ sizeBytes: zipResult.sizeBytes, expiresAt: zipResult.expiresAt });
        const link = document.createElement('a');
        link.href = zipResult.downloadUrl;
        link.download = zipResult.filename || 'project-assets.zip';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setError(zipResult.message || 'The package couldn’t be prepared. Please try again.');
      }
    } catch (zipError) {
      console.error('Error preparing project ZIP:', zipError);
      setError('The package couldn’t be prepared. Please try again.');
    } finally {
      setPackageBusy(false);
    }
  };

  /** Signed download for one image/audio file (the backend checks access). */
  const requestAssetDownload = async (asset: WorkspaceAsset, kind: 'image' | 'audio') => {
    if (!project || !id) return { success: false, error: 'This project isn’t loaded yet. Please try again.' };
    try {
      const token = await user?.getIdToken(true);
      if (!token) return { success: false, error: 'Your session has expired. Sign in again to download.' };
      if (!asset.url) return { success: false, error: 'This asset has no downloadable file.' };
      const result = await downloadAsset(id, asset.url, kind, token);
      if (result.success && result.downloadUrl) {
        return { success: true, downloadUrl: result.downloadUrl, filename: result.filename };
      }
      return { success: false, error: result.message || 'The download didn’t start. Please try again.' };
    } catch (downloadError: any) {
      console.error('Error downloading asset:', downloadError);
      return { success: false, error: friendlyError(downloadError, 'The download didn’t start. Please try again.') };
    }
  };

  const handleDirectAssetDownload = async (asset: WorkspaceAsset, kind: 'image' | 'audio') => {
    const index = (kind === 'image' ? images : sounds).indexOf(asset);
    const key = `${kind}-${asset.id || index}`;
    setAssetBusy((previous) => ({ ...previous, [key]: true }));
    const result = await requestAssetDownload(asset, kind);
    setAssetBusy((previous) => ({ ...previous, [key]: false }));
    if (result.success && result.downloadUrl) {
      const link = document.createElement('a');
      link.href = result.downloadUrl;
      link.download = result.filename || '';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      setError(result.error || 'The download didn’t start. Please try again.');
    }
  };

  const handleAccept = async () => {
    if (!id || responding) return;
    setResponding(true);
    setError(null);
    try {
      const token = await user?.getIdToken(true);
      if (!token) throw new Error('Your session has expired. Sign in again to accept this project.');
      await respondToProjectAssignment(id, true, null, token);
      await refetch();
      setSuccessMessage('Project accepted. It is now in your active work.');
    } catch (acceptError: any) {
      console.error('Error accepting project:', acceptError);
      setError(friendlyError(new Error(getApolloErrorMessage(acceptError)), 'The project couldn’t be accepted. Please try again.'));
    } finally {
      setResponding(false);
    }
  };

  // The reason goes to the creator so they can reassign quickly.
  const handleConfirmDecline = async () => {
    if (!id || responding) return;
    setResponding(true);
    setDeclineError(null);
    try {
      const token = await user?.getIdToken(true);
      if (!token) {
        setDeclineError('Your session has expired. Sign in again to decline this project.');
        return;
      }
      await respondToProjectAssignment(id, false, declineReason.trim() || null, token);
      setDeclineOpen(false);
      navigate('/edit-projects/');
    } catch (declineFailure: any) {
      console.error('Error declining project:', declineFailure);
      setDeclineError(friendlyError(declineFailure, 'The project could not be declined. Please try again.'));
    } finally {
      setResponding(false);
    }
  };

  const handleRevisionStatusChange = async (revisionId: string, status: string, comments?: string) => {
    // Thrown, not returned: the dialog treats a resolved promise as success
    // and shows the reason itself when it fails.
    if (readOnlyNotice) throw new Error(readOnlyNotice);
    try {
      const token = await user?.getIdToken(true);
      if (!token) throw new Error('Your session has expired. Sign in again.');
      await updateRevisionStatus(revisionId, status, comments || '', token);
    } catch (revisionError) {
      console.error('Failed to update revision status:', revisionError);
      throw revisionError;
    }
    // Saved: a failed refresh must not read as a failed decision.
    try {
      await refetch();
    } catch (refreshError) {
      console.error('Failed to refresh after a revision decision:', refreshError);
    }
  };

  // ---- Multicam ---------------------------------------------------------
  const multicamNotes: ReviewAnnotation[] = useMemo(
    () => [...(multicamAnnotationsExport?.annotations || [])].sort((a, b) => (a.timelineTimeMs ?? 0) - (b.timelineTimeMs ?? 0)),
    [multicamAnnotationsExport]
  );
  const multicamAsStudio: StudioAnnotation[] = useMemo(
    () => multicamNotes.map((ann, index) => {
      const isCut = String(ann.type || '').toUpperCase() === 'SWITCH';
      const text = isCut
        ? `Cut to angle ${ann.targetAngleKey || '?'}${ann.text ? ` · ${ann.text}` : ''}`
        : ann.text || 'Note';
      return {
        id: `mc-${ann.timelineTimeMs}-${index}`,
        time: (ann.timelineTimeMs ?? 0) / 1000,
        text,
        category: isCut ? 'scene' : ann.category || 'general',
        color: ann.color || undefined,
        ...(typeof ann.endTimeMs === 'number' && ann.endTimeMs > ann.timelineTimeMs ? { endTime: ann.endTimeMs / 1000 } : {}),
      };
    }),
    [multicamNotes]
  );

  const feedbackNotes = isMulticam ? multicamAsStudio : annotations;
  const currentTime = isMulticam ? multicamClock.ms / 1000 : player.currentTime;
  const playing = isMulticam ? multicamClock.playing : player.playing;
  const mediaDuration = isMulticam
    ? (Number(project?.multicamSnapshot?.timelineDurationMs) || 0) / 1000
    : player.duration || Number(project?.videoDuration) || 0;

  const seekTo = useCallback((seconds: number) => {
    if (isMulticam) {
      setMulticamSeek({ timeMs: Math.max(0, seconds * 1000), nonce: Date.now() });
    } else {
      player.seek(seconds);
    }
  }, [isMulticam, player]);

  // The creator's paper edit in the transcript, read-only: shown once the
  // creator marked words Remove or Short (the words' times load only then).
  // Notes show in it too; camera cuts stay out.
  const transcriptMarking = useMemo<TranscriptMarkingProps | null>(() => {
    const marks = transcriptMarksFrom(isMulticam
      ? multicamAsStudio.filter((_, index) => String(multicamNotes[index]?.type || '').toUpperCase() !== 'SWITCH')
      : annotations);
    if (!marks.some((mark) => mark.kind !== 'note')) return null;
    return {
      marks,
      readOnly: true,
      selectedMarkId: selectedNoteId,
      onSelectMark: (mark) => {
        setSelectedNoteId(mark.id);
        seekTo(mark.start);
      },
    };
  }, [annotations, isMulticam, multicamAsStudio, multicamNotes, seekTo, selectedNoteId]);

  const tasksByAnnotation = useMemo(() => {
    const map = new Map<string, WorkspaceTask[]>();
    for (const task of tasks) {
      for (const annotationId of task.generatedFromAnnotations || []) {
        map.set(annotationId, [...(map.get(annotationId) || []), task]);
      }
    }
    return map;
  }, [tasks]);

  const activeKey = useMemo(
    () => annotationsActiveAt(feedbackNotes, currentTime).map((a) => a.id).join('|'),
    [feedbackNotes, currentTime]
  );
  const activeIds = useMemo(() => (activeKey ? activeKey.split('|') : []), [activeKey]);

  const stageNotes = useMemo(() => {
    const ids = new Set(activeIds);
    return annotations.filter((a) => ids.has(a.id)).map((annotation) => {
      const category = getAnnotationCategory(annotation.category, annotation.color);
      return { id: annotation.id, label: `${category.label} · ${formatTimecode(annotation.time, fps)}`, text: annotation.text, color: category.color };
    });
  }, [annotations, activeIds, fps]);

  const feedbackLane = useMemo<TimelineLane>(() => ({
    id: 'feedback',
    label: isMulticam ? 'Notes & cuts' : 'Feedback',
    icon: isMulticam ? <ContentCutIcon /> : <ChatBubbleOutlineIcon />,
    kind: 'markers',
    maxRows: 3,
    emptyText: 'No time-coded feedback',
    items: feedbackNotes.map((annotation, index) => {
      const category = getAnnotationCategory(annotation.category, annotation.color);
      return {
        id: annotation.id,
        start: annotation.time,
        end: isRangeAnnotation(annotation) ? (annotation.endTime as number) : null,
        label: annotation.text,
        color: category.color,
        ariaLabel: `Note ${index + 1} (${category.label}): ${annotation.text}`,
        tooltip: (
          <Box>
            <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: neutral[300] }}>{category.label}</Typography>
            <Typography sx={{ fontSize: 12.5, lineHeight: 1.45 }}>{annotation.text}</Typography>
          </Box>
        ),
      };
    }),
  }), [feedbackNotes, isMulticam]);

  const readOnlyTasksLane = useMemo<TimelineLane>(() => ({
    id: 'tasks',
    label: 'Tasks',
    icon: <TaskAltIcon />,
    kind: 'tasks',
    maxRows: 3,
    emptyText: tasksPending ? 'Generating tasks…' : 'No tasks yet',
    items: tasks
      .filter((task) => taskStart(task) !== null)
      .map((task) => {
        const status = taskStatusMeta(task.status);
        const start = taskStart(task) as number;
        return {
          id: task.id,
          start,
          end: taskEnd(task) ?? start + 0.1,
          label: task.title,
          color: status.color,
          ariaLabel: `${task.title} (${status.label})`,
        };
      }),
  }), [tasks, tasksPending]);

  const assetLanes = useMemo<TimelineLane[]>(() => {
    const lane = (laneId: string, label: string, icon: React.ReactNode, list: WorkspaceAsset[], color: string): TimelineLane => ({
      id: laneId,
      label,
      icon,
      kind: 'assets',
      maxRows: 3,
      emptyText: `No ${label.toLowerCase()} placed`,
      items: list
        .map((asset, index) => ({ asset, index }))
        .filter(({ asset }) => typeof asset.startTime === 'number')
        .map(({ asset, index }) => {
          const start = asset.startTime as number;
          const end = typeof asset.endTime === 'number' && asset.endTime > start ? asset.endTime : start + 1;
          return {
            id: `${laneId}-${asset.id || index}`,
            start,
            end,
            label: asset.notes || `${label.replace(/s$/, '')} ${index + 1}`,
            color,
            icon,
          };
        }),
    });
    // Assets aren't annotation categories, so they stay neutral: two shades
    // of slate that still tell the lanes apart.
    return [
      lane('images', 'Images', <ImageOutlinedIcon />, images, brand.slate),
      lane('audio', 'Audio', <MusicNoteOutlinedIcon />, sounds, neutral[700]),
    ];
  }, [images, sounds]);

  const timelineExtraLanes = useMemo(() => [feedbackLane, ...assetLanes], [feedbackLane, assetLanes]);
  const feedbackTabLanes = useMemo(() => [feedbackLane, readOnlyTasksLane], [feedbackLane, readOnlyTasksLane]);

  const markerAnnotations = feedbackNotes;
  const versions = useMemo(() => buildRevisionVersions(revisions, notes), [revisions, notes]);
  const revisionCount = Number(project?.revisionCount) || 0;
  const revisionLimit = resolveRevisionLimit(project?.revisionLimit);
  const guidance = nextStepGuidance(project?.status, {
    role: userRole,
    versionCount: versions.length,
    revisionCount,
    revisionLimit,
  });
  const summary = summarizeTasks(tasks);
  const creatorName = useMemo(() => {
    if (project?.creatorName) return project.creatorName;
    const note = notes.find((n) => n?.authorId && n.authorId === project?.userId) || notes.find((n) => String(n?.authorRole).toLowerCase() === 'creator');
    return note?.authorName || 'the creator';
  }, [notes, project?.userId, project?.creatorName]);

  const playerTab = tab === 'feedback' || tab === 'timeline';
  // Multicam keeps its wide review player above the timeline.
  const timelineLayout = tab === 'timeline' && !isMulticam;

  // The transcript beside the player: the project's video, or the multicam
  // set's sound (its separate recording, else its sound angle) placed by its sync.
  const transcriptSource = useMemo(() => {
    if (!project) return null;
    if (isMulticam) {
      const snapshot = project.multicamSnapshot;
      const angles = safeArray<any>(snapshot?.angles);
      const audio = angles.find((angle) => angle.angleKey === snapshot?.audioAngleKey)
        ?? angles.find((angle) => angle.angleKey === 'A')
        ?? angles[0];
      return transcriptSourceOf(snapshot?.audioSource, audio);
    }
    return project.videoId ? { videoId: String(project.videoId), placement: null } : null;
  }, [isMulticam, project]);
  // Who says each transcript line, when the creator's studio worked out who speaks when.
  const transcriptSpeakers = useMemo(
    () => (isMulticam ? createSpeakerLookup(project?.multicamSnapshot?.speakingActivity, safeArray<any>(project?.multicamSnapshot?.angles)) : undefined),
    [isMulticam, project?.multicamSnapshot]
  );

  // The set's separate sound recording, heard in the review player instead of an angle.
  const separateRecording = useMemo(
    () => (isMulticam ? recordingAngle(project?.multicamSnapshot?.audioSource) : null),
    [isMulticam, project?.multicamSnapshot?.audioSource]
  );

  // Pause playback when leaving the player tabs.
  useEffect(() => {
    if (!playerTab && player.playing) player.pause();
  }, [playerTab, player]);

  // Keyboard shortcuts while the player is on screen.
  const shortcutRef = useRef<(event: KeyboardEvent) => void>(() => undefined);
  shortcutRef.current = (event: KeyboardEvent) => {
    if (!playerTab || event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return;
    if (helpOpen || taskDialog.open || uploadRevisionModalOpen || declineOpen || Boolean(taskToDelete) || Boolean(assetPreview)) return;
    if (isTypingTarget(event.target)) return;
    if (event.key === '?') {
      event.preventDefault();
      setHelpOpen(true);
      return;
    }
    const target = event.target as HTMLElement | null;
    const nativeControl = target && ['BUTTON', 'A'].includes(target.tagName);
    if (event.code === 'BracketLeft' || event.code === 'BracketRight') {
      event.preventDefault();
      const next = adjacentAnnotationTime(feedbackNotes, currentTime, event.code === 'BracketRight' ? 1 : -1);
      if (next !== null) {
        seekTo(next);
        const match = feedbackNotes.find((annotation) => annotation.time === next);
        if (match) setSelectedNoteId(match.id);
      }
      return;
    }
    if (event.code === 'Equal' || event.code === 'NumpadAdd') { event.preventDefault(); timelineZoomRef.current?.zoomIn(); return; }
    if (event.code === 'Minus' || event.code === 'NumpadSubtract') { event.preventDefault(); timelineZoomRef.current?.zoomOut(); return; }
    if (isMulticam || !player.element) return;
    switch (event.code) {
      case 'Space':
        if (nativeControl) return;
        event.preventDefault();
        player.toggle();
        break;
      case 'KeyK': event.preventDefault(); player.pause(); break;
      case 'KeyJ': event.preventDefault(); player.shuttleReverse(); break;
      case 'KeyL': event.preventDefault(); player.shuttleForward(); break;
      case 'ArrowLeft': event.preventDefault(); player.skip(event.shiftKey ? -10 : -5); break;
      case 'ArrowRight': event.preventDefault(); player.skip(event.shiftKey ? 10 : 5); break;
      case 'Comma': event.preventDefault(); player.stepFrames(-1); break;
      case 'Period': event.preventDefault(); player.stepFrames(1); break;
      case 'KeyM': event.preventDefault(); player.toggleMute(); break;
      case 'KeyF': event.preventDefault(); fullscreen.toggle(); break;
      default: break;
    }
  };
  useEffect(() => {
    const listener = (event: KeyboardEvent) => shortcutRef.current(event);
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, []);

  const renderLinkedTasks = useCallback((annotation: StudioAnnotation) => {
    const linked = tasksByAnnotation.get(annotation.id) || [];
    if (linked.length === 0) return null;
    return (
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
        {linked.map((task) => {
          const status = taskStatusMeta(task.status);
          return (
            <Box
              key={task.id}
              component="span"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.25, borderRadius: '6px', border: `1px solid ${brand.border}`, bgcolor: 'background.paper', fontSize: 12, maxWidth: '100%' }}
            >
              <TaskAltIcon sx={{ fontSize: 14, color: status.color }} />
              <Box component="span" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{task.title}</Box>
              <Box component="span" sx={{ color: 'text.secondary', fontWeight: 600, whiteSpace: 'nowrap' }}>{status.label}</Box>
            </Box>
          );
        })}
      </Box>
    );
  }, [tasksByAnnotation]);

  const handleFpsChange = (rate: number) => {
    setFpsOverride(rate);
    try {
      if (id) window.localStorage.setItem(`${FPS_STORAGE_PREFIX}${id}`, String(rate));
    } catch {
      /* storage unavailable */
    }
  };

  const downloadJson = (filename: string, value: unknown) =>
    downloadTextFile(filename, JSON.stringify(value, null, 2), 'application/json;charset=utf-8');

  // Only the first load replaces the page. With notifyOnNetworkStatusChange,
  // `loading` is also true during every refetch.
  if (loading && !project) {
    return (
      <Box sx={{ bgcolor: brand.canvas, minHeight: '100%' }}>
        <Box sx={{ maxWidth: 1680, mx: 'auto', px: { xs: 2, md: 3 }, pt: { xs: 2, md: 2.5 }, pb: 6 }}>
          <LoadingState variant="detail" label="Loading the project" />
        </Box>
      </Box>
    );
  }

  // Only a missing project justifies replacing the whole workspace.
  if (!project) {
    // The server answered (not found, not yours): retrying won't help. A
    // failed request might.
    const unreachable = Boolean(queryError) && (Boolean(queryError?.networkError) || !queryError?.graphQLErrors?.length);
    return (
      <Box sx={{ bgcolor: brand.canvas, minHeight: '100%' }}>
        <Box sx={{ maxWidth: 800, mx: 'auto', px: { xs: 2, md: 3 }, pt: { xs: 3, md: 4 }, pb: 6 }}>
          <Alert
            severity="error"
            action={
              unreachable ? (
                <Button color="inherit" size="small" onClick={() => void refetch()}>
                  Retry
                </Button>
              ) : undefined
            }
          >
            {unreachable
              ? 'We couldn’t load this project. Check your connection and try again.'
              : 'This project isn’t available. It may have been removed, or it isn’t assigned to you.'}
          </Alert>
          <Button sx={{ mt: 2 }} variant="outlined" onClick={() => navigate(userRole === 'creator' ? '/my-projects' : '/edit-projects/')}>
            Back to projects
          </Button>
        </Box>
      </Box>
    );
  }

  const inspectedTask = inspectedTaskId ? tasks.find((task) => task.id === inspectedTaskId) ?? null : null;
  const taskPendingDelete = taskToDelete ? tasks.find((task) => task.id === taskToDelete) ?? null : null;
  const sourceLabel = isMulticam && project.multicamSnapshot
    ? `${project.multicamSnapshot.name} (${project.multicamSnapshot.angles?.length || 0} angles)`
    : project.videoName;
  const subtitle = [
    userRole === 'creator' && project.editor?.name ? `Editor: ${project.editor.name}` : `From ${creatorName}`,
    sourceLabel,
    project.createdAt ? `Requested ${formatDate(project.createdAt)}` : null,
  ].filter(Boolean).join(' · ');
  const awaitingResponse = userRole === 'editor' && awaitingEditorResponse(project.status, Boolean(project.editor || project.editorId));

  const notesHeaderActions = (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
      {isMulticam && (
        <Tooltip title="Reload the creator's multicam notes">
          <span>
            <IconButton
              size="small"
              aria-label="Refresh notes"
              onClick={() => loadMulticamAnnotationsExport()}
              disabled={multicamAnnotationsLoading || !project.multicamAnnotationsSnapshotId}
            >
              {multicamAnnotationsLoading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      )}
      <MarkerExportMenu
        annotations={markerAnnotations}
        fps={fps}
        fpsAssumed={!fpsKnown && fpsOverride === null}
        title={project.projectName}
        sourceName={project.videoName}
        onExported={(filename) => setSuccessMessage(`Exported ${filename}`)}
      />
    </Box>
  );

  return (
    <Box sx={{ bgcolor: brand.canvas, minHeight: '100%' }}>
      <Box sx={{ maxWidth: 1680, mx: 'auto', px: { xs: 2, md: 3 }, pt: { xs: 2, md: 2.5 }, pb: 6 }}>
        {readOnlyNotice && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {readOnlyNotice}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <WorkspaceHeader
          projectName={project.projectName}
          subtitle={subtitle}
          status={project.status}
          isMulticam={isMulticam}
          deadline={project.deadline}
          priority={project.priority}
          revisionCount={revisionCount}
          revisionLimit={revisionLimit}
          finished={isFinished}
          tasksDone={summary.completed}
          tasksTotal={summary.total}
          guidance={guidance}
          onBack={() => navigate(userRole === 'creator' ? '/my-projects' : '/edit-projects/')}
          viewer={userRole}
          hasEditor={Boolean(project.editor || project.editorId)}
          nextVersion={versions.length + 1}
          awaitingResponse={awaitingResponse}
          responding={responding}
          onAccept={() => void handleAccept()}
          onDecline={() => { setDeclineReason(''); setDeclineError(null); setDeclineOpen(true); }}
          canUpload={!isFinished}
          onUpload={userRole === 'editor' ? () => { if (!isFinished) setUploadRevisionModalOpen(true); } : undefined}
          downloading={packageBusy}
          onDownloadPackage={() => void handleDownloadPackage()}
          metaExtra={<FrameRateChip fps={fps} known={fpsKnown} chosen={fpsOverride !== null} onChange={handleFpsChange} />}
        />

        <Box sx={{ borderBottom: `1px solid ${brand.border}`, mb: 2.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="Project workspace sections"
            sx={{
              flex: 1,
              minHeight: 44,
              '& .MuiTab-root': { minHeight: 44, textTransform: 'none', fontWeight: 600, fontSize: 14, px: 1.75 },
              '& .Mui-selected': { color: `${brand.ink} !important` },
              '& .MuiTabs-indicator': { bgcolor: brand.copper, height: 2.5, borderRadius: 2 },
            }}
          >
            <Tab value="feedback" label={tabLabel('Feedback', feedbackNotes.length)} />
            <Tab value="tasks" label={tabLabel('Tasks', tasks.length ? `${summary.completed}/${summary.total}` : tasksPending ? '…' : 0)} />
            <Tab value="timeline" label={tabLabel('Timeline')} />
            <Tab value="assets" label={tabLabel('Assets & downloads', images.length + sounds.length || undefined)} />
            <Tab value="deliver" label={tabLabel('Deliver', versions.length || undefined)} />
            <Tab value="brief" label={tabLabel('Brief')} />
            <Tab value="messages" label={tabLabel('Messages', notes.length || undefined)} />
          </Tabs>
          {playerTab && (
            <Tooltip title="Keyboard shortcuts (?)">
              <IconButton aria-label="Keyboard shortcuts" onClick={() => setHelpOpen(true)} size="small">
                <KeyboardIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Feedback + Timeline share one player so playback survives switching. */}
        <Box
          sx={{
            display: playerTab ? 'grid' : 'none',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 380px', xl: 'minmax(0, 1fr) 420px' },
            // The player keeps its place in the tree (so playback survives
            // switching tabs); CSS moves it to the side on the Timeline tab.
            gridTemplateAreas: {
              xs: '"player" "lanes" "panel"',
              lg: timelineLayout ? '"lanes player" "lanes panel"' : '"player panel" "lanes panel"',
            },
            gridTemplateRows: { lg: 'auto 1fr' },
            gap: 2.5,
            alignItems: 'start',
          }}
        >
          <Box sx={{ gridArea: 'player', minWidth: 0 }}>
            {isMulticam && project.multicamSnapshot ? (
              <>
                {multicamAnnotationsError && <Alert severity="warning" sx={{ mb: 2 }}>{multicamAnnotationsError}</Alert>}
                {userRole === 'editor' && !multicamHintDismissed && (
                  <Alert
                    severity="info"
                    variant="outlined"
                    onClose={dismissMulticamHint}
                    closeText="Dismiss"
                    sx={{ mb: 2, bgcolor: 'background.paper' }}
                    data-testid="multicam-editor-hint"
                  >
                    Every camera plays in sync below. Read the conversation under Transcript beside the player, and
                    export the creator&apos;s cut for Premiere Pro, DaVinci Resolve or Final Cut Pro from Assets &amp;
                    downloads.
                    <Box sx={{ mt: 1 }}>
                      <Button size="small" variant="outlined" onClick={() => setTab('assets')}>
                        Export rough cut
                      </Button>
                    </Box>
                  </Alert>
                )}
                {/* Sits straight in the page grid, so it keeps its own panel frame. */}
                <MulticamReviewPlayer
                  angles={project.multicamSnapshot.angles || []}
                  audioAngleKey={project.multicamSnapshot.audioAngleKey || 'A'}
                  audioSource={separateRecording}
                  timelineDurationMs={project.multicamSnapshot.timelineDurationMs}
                  annotations={multicamNotes}
                  seekRequest={multicamSeek}
                  fps={fps}
                  onTimeChange={(ms, isPlaying) => setMulticamClock({ ms, playing: isPlaying })}
                />
              </>
            ) : project.videoUrl ? (
              <Box
                ref={fullscreen.ref}
                sx={{
                  borderRadius: fullscreen.isFullscreen ? 0 : 3,
                  overflow: 'hidden',
                  bgcolor: 'common.black',
                  display: 'flex',
                  flexDirection: 'column',
                  ...(fullscreen.isFullscreen ? { height: '100vh' } : {}),
                }}
                data-project-video
              >
                <VideoStage
                  videoUrl={project.videoUrl}
                  player={player}
                  testId="workspace-video-player"
                  notes={timelineLayout && !fullscreen.isFullscreen ? [] : stageNotes}
                  fullscreen={fullscreen.isFullscreen}
                  maxHeight="min(46vh, 560px)"
                />
                <TransportBar
                  player={player}
                  fps={fps}
                  compact={timelineLayout}
                  markers={annotations.map((annotation) => ({
                    time: annotation.time,
                    endTime: annotation.endTime,
                    color: getAnnotationCategory(annotation.category, annotation.color).color,
                  }))}
                  isFullscreen={fullscreen.isFullscreen}
                  onToggleFullscreen={fullscreen.toggle}
                />
              </Box>
            ) : (
              <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 3, borderColor: brand.border }}>
                <Typography color="text.secondary">The source video isn’t available for this project.</Typography>
              </Paper>
            )}
          </Box>

          <Box sx={{ gridArea: 'lanes', minWidth: 0 }}>
            {tab === 'timeline' ? (
              <InteractiveTimeline
                project={{ ...project, videoDuration: mediaDuration || project.videoDuration }}
                editable={canEditTaskList && !isFinished}
                currentTime={currentTime}
                playing={playing}
                duration={mediaDuration}
                fps={fps}
                extraLanes={timelineExtraLanes}
                title="Edit timeline"
                hideDetails
                onTaskSelect={setInspectedTaskId}
                onTasksUpdated={() => refetch()}
                onSeekTo={seekTo}
              />
            ) : (
              <ProTimeline
                ref={(handle) => { timelineZoomRef.current = handle; }}
                title="Timeline"
                duration={mediaDuration}
                currentTime={currentTime}
                playing={playing}
                fps={fps}
                lanes={feedbackTabLanes}
                selectedItemId={selectedNoteId}
                onSeek={seekTo}
                onItemSelect={(item, lane) => {
                  if (lane.id === 'feedback') setSelectedNoteId(item.id);
                }}
                onItemActivate={(item, lane) => {
                  if (lane.id === 'tasks') {
                    setInspectedTaskId(item.id);
                    setTab('timeline');
                  }
                }}
                data-testid="feedback-timeline"
              />
            )}
          </Box>

          <Box sx={{ gridArea: 'panel', position: { lg: timelineLayout ? 'static' : 'sticky' }, top: { lg: 16 }, minWidth: 0 }}>
            {tab === 'timeline' ? (
              inspectedTask ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="subtitle2" component="h2" sx={{ fontWeight: 700, color: 'text.secondary' }}>Selected task</Typography>
                  <TaskCard
                    task={inspectedTask}
                    linked={(inspectedTask.generatedFromAnnotations || []).map((annotationId) => annotationsById.get(annotationId)).filter((a): a is StudioAnnotation => Boolean(a))}
                    fps={fps}
                    editable={canEditTaskList && !isFinished}
                    updating={Boolean(taskUpdating[inspectedTask.id])}
                    onStatusChange={(taskId, status) => void handleTaskStatusChange(taskId, status)}
                    onEdit={openEditTask}
                    onDelete={(taskId) => setTaskToDelete(taskId)}
                    onJumpTo={seekTo}
                  />
                </Box>
              ) : (
                <Box data-testid="timeline-help">
                  <Section title="Working on the timeline">
                    {[
                      'Click a task to see its details and the feedback behind it.',
                      canEditTaskList && !isFinished ? 'Drag a task to move it; drag its edges to change its in and out.' : 'Tasks are read-only here.',
                      'Double-click a task to edit it; right-click for more.',
                      'Click the ruler or an empty lane to move the playhead. Ctrl/⌘ + scroll zooms.',
                    ].map((line) => (
                      <Typography key={line} variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>{line}</Typography>
                    ))}
                    <Typography variant="caption" component="p" sx={{ fontWeight: 700, color: 'text.secondary', mt: 1.5, mb: 0.75 }}>Task colours</Typography>
                    <Box component="ul" sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', listStyle: 'none', m: 0, p: 0 }}>
                      {['PENDING', 'IN_PROGRESS', 'COMPLETED'].map((status) => (
                        <Box component="li" key={status} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: '0.8125rem' }}>
                          <Box component="span" aria-hidden="true" sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: taskStatusMeta(status).color }} />
                          {taskStatusMeta(status).label}
                        </Box>
                      ))}
                    </Box>
                  </Section>
                </Box>
              )
            ) : (
              <NotesTranscriptSwitch
                notesLabel={isMulticam ? 'Notes & cuts' : 'Feedback'}
                notes={(
                  <AnnotationPanel
                    title={isMulticam ? 'Notes & cuts' : 'Feedback'}
                    annotations={feedbackNotes}
                    fps={fps}
                    activeIds={activeIds}
                    selectedId={selectedNoteId}
                    playing={playing}
                    readOnly
                    onSeek={(annotation) => {
                      setSelectedNoteId(annotation.id);
                      seekTo(annotation.time);
                    }}
                    headerActions={notesHeaderActions}
                    renderExtra={isMulticam ? undefined : renderLinkedTasks}
                    emptyTitle={multicamAnnotationsLoading ? 'Loading notes…' : 'No time-coded feedback'}
                    emptyHint={isMulticam ? 'The creator left no multicam notes or cuts.' : 'The creator did not leave notes on the video. Work from the brief.'}
                    listMaxHeight={transcriptSource ? 'calc(100vh - 350px)' : 'calc(100vh - 300px)'}
                    testId="feedback-panel"
                  />
                )}
                transcript={transcriptSource ? (
                  <TranscriptPanel
                    videoId={transcriptSource.videoId}
                    placement={transcriptSource.placement}
                    currentTime={currentTime}
                    playing={playing}
                    fps={fps}
                    onSeek={seekTo}
                    canStart
                    exportName={project.projectName || 'project'}
                    speakerAt={transcriptSpeakers}
                    marking={transcriptMarking}
                    listMaxHeight="calc(100vh - 350px)"
                  />
                ) : null}
              />
            )}
          </Box>
        </Box>

        {visitedTabs.has('tasks') && (
          <Box sx={{ display: tab === 'tasks' ? 'block' : 'none' }} role="tabpanel" aria-label="Tasks">
            <TaskBoard
              tasks={tasks}
              annotationsById={annotationsById}
              fps={fps}
              editable={canEditTaskList}
              finished={isFinished}
              autoGenerating={autoGenerating}
              taskUpdating={taskUpdating}
              onCreate={openCreateTask}
              onStatusChange={(taskId, status) => void handleTaskStatusChange(taskId, status)}
              onEdit={openEditTask}
              onDelete={(taskId) => setTaskToDelete(taskId)}
              onJumpTo={(seconds) => {
                setTab('feedback');
                seekTo(seconds);
              }}
              actions={(
                <TaskManagementPanel
                  variant="toolbar"
                  project={project}
                  editable={canEditTaskList}
                  readOnly={isFinished}
                  autoOpenAIPreview={openAIPreviewRequested}
                  tasksPending={tasksPending}
                  onAutoOpenAIPreviewHandled={() => {
                    // Clear the request so a reload or back navigation doesn't reopen it.
                    navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: null });
                  }}
                  onTasksUpdated={() => { refetch(); }}
                />
              )}
            />
          </Box>
        )}

        {visitedTabs.has('assets') && (
          <Box sx={{ display: tab === 'assets' ? 'block' : 'none' }} role="tabpanel" aria-label="Assets and downloads">
            <AssetsTab
              projectName={project.projectName}
              videoName={project.videoName}
              videoUrl={project.videoUrl}
              durationSeconds={mediaDuration}
              images={images}
              sounds={sounds}
              referenceLinks={safeArray<string>(project.referenceLinks).filter(Boolean)}
              referenceFiles={safeArray<string>(project.referenceFiles).filter(Boolean)}
              angles={isMulticam ? safeArray<any>(project.multicamSnapshot?.angles) : []}
              audioAngleKey={project.multicamSnapshot?.audioAngleKey}
              audioSource={isMulticam ? project.multicamSnapshot?.audioSource ?? null : null}
              multicamAnnotations={isMulticam && (!project.multicamAnnotationsSnapshotId || multicamAnnotationsExport) ? multicamNotes : undefined}
              markerAnnotations={markerAnnotations}
              fps={fps}
              fpsKnown={fpsKnown}
              fpsChosen={fpsOverride !== null}
              onFpsChange={handleFpsChange}
              packageBusy={packageBusy}
              packageInfo={packageInfo}
              onDownloadPackage={() => void handleDownloadPackage()}
              onPreviewAsset={(asset, kind) => setAssetPreview({ ...asset, type: kind, name: asset.notes || `${kind === 'image' ? 'Image' : 'Audio'} ${asset.id || ''}`.trim() })}
              onDownloadAsset={(asset, kind) => void handleDirectAssetDownload(asset, kind)}
              assetBusy={assetBusy}
              onDownloadMulticamJson={isMulticam ? () => downloadJson(`multicam-${project.id}.json`, project.multicamSnapshot) : undefined}
              onDownloadAnnotationsJson={isMulticam ? async () => {
                try {
                  const result = await exportAnnotationsJson(project.id);
                  downloadJson(`annotations-${project.id}.json`, result);
                } catch (exportError: any) {
                  console.error('Failed to export annotations:', exportError);
                  setError('Could not export the multicam annotations. Please try again.');
                }
              } : undefined}
              onExported={(filename) => setSuccessMessage(`Exported ${filename}`)}
            />
          </Box>
        )}

        {visitedTabs.has('deliver') && (
          <Box sx={{ display: tab === 'deliver' ? 'block' : 'none' }} role="tabpanel" aria-label="Deliver">
            <DeliverTab
              revisions={revisions}
              notes={notes}
              userRole={userRole}
              finished={isFinished}
              revisionCount={revisionCount}
              revisionLimit={revisionLimit}
              active={tab === 'deliver'}
              fps={fps}
              editorName={project.editor?.name || null}
              onUpload={() => { if (!isFinished) setUploadRevisionModalOpen(true); }}
              onRevisionStatusChange={handleRevisionStatusChange}
              onDecided={setSuccessMessage}
            />
          </Box>
        )}

        {visitedTabs.has('brief') && (
          <Box sx={{ display: tab === 'brief' ? 'block' : 'none' }} role="tabpanel" aria-label="Brief">
            <BriefTab
              description={project.description}
              comments={project.comments}
              targetAudience={project.targetAudience}
              desiredStyle={project.desiredStyle}
              musicPreference={project.musicPreference}
              colorPreference={project.colorPreference}
              brandGuidelines={project.brandGuidelines}
              deliverables={project.deliverables}
              referenceLinks={safeArray<string>(project.referenceLinks).filter(Boolean)}
              deadline={project.deadline}
              priority={project.priority}
              estimatedComplexity={project.estimatedComplexity}
              createdAt={project.createdAt}
              finished={isFinished}
              creatorName={creatorName}
            />
          </Box>
        )}

        {visitedTabs.has('messages') && (
          <Box sx={{ display: tab === 'messages' ? 'block' : 'none', maxWidth: 920 }} role="tabpanel" aria-label="Messages">
            <ProjectNotes
              projectId={id || ''}
              notes={notes}
              onNoteAdded={() => { refetch(); }}
            />
          </Box>
        )}
      </Box>

      {/* Upload Revision Modal */}
      <UploadRevisionModal
        projectId={id || ''}
        nextVersion={versions.length + 1}
        open={uploadRevisionModalOpen}
        onClose={() => setUploadRevisionModalOpen(false)}
        onUploaded={() => {
          refetch();
          setTab('deliver');
        }}
      />

      <TaskFormDialog
        open={taskDialog.open}
        task={taskDialog.task}
        fps={fps}
        defaultStart={currentTime}
        saving={savingTask}
        disabled={isFinished}
        onClose={() => setTaskDialog({ open: false, task: null })}
        onSave={(values) => void handleSaveTask(values)}
      />

      <ShortcutHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} groups={WORKSPACE_SHORTCUTS} />

      {/* Decline with a reason (sent to the creator) */}
      <Dialog open={declineOpen} onClose={() => { if (!responding) setDeclineOpen(false); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Decline this project?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>{project.projectName}</Typography>
          <TextField
            label="Reason (optional)"
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            multiline
            minRows={3}
            fullWidth
            autoFocus
            inputProps={{ maxLength: 1000 }}
            helperText="Sent to the creator, so they can reassign the project quickly."
          />
          {declineError && <Alert severity="error" sx={{ mt: 2 }}>{declineError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeclineOpen(false)} disabled={responding}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void handleConfirmDecline()}
            disabled={responding}
            startIcon={responding ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {responding ? 'Declining…' : 'Decline project'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Notification Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Asset Preview Modal */}
      <AssetPreviewModal
        open={Boolean(assetPreview)}
        onClose={() => setAssetPreview(null)}
        asset={assetPreview}
        fps={fps}
        onDownload={async () => {
          if (!assetPreview) return { success: false, error: 'No asset selected' };
          return requestAssetDownload(assetPreview, assetPreview.type);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={Boolean(taskToDelete)} onClose={() => setTaskToDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete this task?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {taskPendingDelete?.title ? `“${taskPendingDelete.title}” and your notes on it are removed. ` : ''}This can’t be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setTaskToDelete(null)}>Cancel</Button>
          <Button
            onClick={() => void confirmDeleteTask()}
            color="error"
            variant="contained"
            disabled={isFinished || (taskToDelete ? taskUpdating[taskToDelete] : false)}
            startIcon={taskToDelete && taskUpdating[taskToDelete] ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Delete task
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EditProjects;
